import { Link } from "react-router-dom";
import { Check, ChevronRight, Dumbbell, ListOrdered } from "lucide-react";
import { useCustomRoutineName } from "@/hooks/useCustomNames";
import Shimmer from "@/components/ui/shimmer";

interface RoutineCardProps {
  routine: {
    id: string;
    name: string;
    image_url?: string;
    exercises?: any[];
    plan_mode?: "days" | "sequential";
    sequential_index?: number;
    plan_routine_count?: number;
  };
  completed: boolean;
}

export const RoutineCard = ({ routine, completed }: RoutineCardProps) => {
  // URL de la imagen por defecto
  const defaultImageUrl = "/lovable-uploads/9b33d3f0-bab1-4172-8480-b92ac4cfbd73.png";
  
  // Usar el hook para obtener el nombre personalizado
  const { customName, isLoading } = useCustomRoutineName(routine?.id || '');
  
  const exerciseCount = routine.exercises?.length || 0;

  return (
    <Link to={`/rutina/${routine.id}`}>
      <div className="relative group">
        <div 
          className={`rounded-2xl h-48 relative overflow-hidden transition-all duration-300 
            ${completed 
              ? 'opacity-80 hover:opacity-100' 
              : 'hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/20'
            }`}
          style={{
            background: routine.image_url 
              ? `url('${routine.image_url}')`
              : `url('${defaultImageUrl}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Multi-layer gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Glassmorphism overlay on hover */}
          <div className="absolute inset-0 backdrop-blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Top badges */}
          <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between gap-2">
            {exerciseCount > 0 && !completed ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-black/40 backdrop-blur-sm text-white/90 border border-white/10">
                <Dumbbell className="h-3 w-3" />
                {exerciseCount} ejercicios
              </span>
            ) : <span />}
            {routine.plan_mode === "sequential" && routine.sequential_index && routine.plan_routine_count ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-primary/90 text-primary-foreground border border-primary-foreground/10">
                <ListOrdered className="h-3 w-3" />
                {routine.sequential_index} de {routine.plan_routine_count}
              </span>
            ) : null}
          </div>

          {/* Bottom content */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex justify-between items-center">
              {isLoading ? (
                <Shimmer className="h-5 w-28" />
              ) : (
                <h3 className="text-lg font-heading font-semibold text-white group-hover:text-primary-foreground transition-colors">
                  {customName || routine.name}
                </h3>
              )}
              <ChevronRight className="h-5 w-5 text-primary opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </div>
            
            {/* Progress indicator for incomplete */}
            {!completed && (
              <div className="mt-2 h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary/60 rounded-full transition-all duration-500 group-hover:bg-primary"
                  style={{ width: '0%' }}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Completed overlay */}
        {completed && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl backdrop-blur-sm transition-opacity group-hover:bg-black/40">
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 rounded-full bg-green-500/20 border border-green-500/30">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-sm text-white/80 font-medium">Completada</span>
            </div>
          </div>
        )}
        
        {/* Pulse animation for incomplete routines */}
        {!completed && (
          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-2xl opacity-0 group-hover:opacity-100 -z-10 blur-sm transition-opacity duration-300" />
        )}
      </div>
    </Link>
  );
};
